import { useState, useEffect } from "react";
import { MessageCircle, X } from "lucide-react";

const WHATSAPP_NUMBER = "918369033791";
const DEFAULT_MESSAGE = "Hi! I'm interested in planning a trip with Dnotours. Can you help?";

const quickMessages = [
  { label: "Plan a Trip", msg: "Hi! I'd like to plan a trip. Can you help me with options?" },
  { label: "Get a Quote", msg: "Hi! I'd like to get a quote for a travel package." },
  { label: "Wedding Inquiry", msg: "Hi! I'm interested in your wedding destination services." },
  { label: "Corporate Travel", msg: "Hi! I need information about corporate travel packages." },
];

export default function WhatsAppFloat() {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const openWhatsApp = (message) => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="whatsapp-float">
      {/* Quick message popup */}
      {isOpen && (
        <div
          className="absolute bottom-[72px] right-0 w-72 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
          data-testid="whatsapp-popup"
        >
          {/* Header */}
          <div className="bg-[#075E54] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Dnotours</p>
                <p className="text-white/70 text-xs">Typically replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="whatsapp-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat bubble */}
          <div className="px-5 py-4 bg-[#ECE5DD]">
            <div className="bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-sm max-w-[85%]">
              <p className="text-slate-700 text-sm leading-relaxed">
                Hey there! How can we help you plan your next adventure?
              </p>
              <span className="text-[10px] text-slate-400 mt-1 block text-right">now</span>
            </div>
          </div>

          {/* Quick replies */}
          <div className="px-4 py-3 space-y-2">
            {quickMessages.map((item) => (
              <button
                key={item.label}
                onClick={() => openWhatsApp(item.msg)}
                className="w-full text-left text-sm px-4 py-2.5 rounded-full border border-[#25D366] text-[#075E54] hover:bg-[#25D366] hover:text-white transition-all duration-200 font-medium"
                data-testid={`whatsapp-quick-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Direct chat */}
          <div className="px-4 pb-4">
            <button
              onClick={() => openWhatsApp(DEFAULT_MESSAGE)}
              className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 transition-colors text-sm"
              data-testid="whatsapp-start-chat-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Start Chat
            </button>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen
            ? "bg-slate-600 hover:bg-slate-700"
            : "bg-[#25D366] hover:bg-[#20BD5A]"
        }`}
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
          </>
        )}
      </button>
    </div>
  );
}
